import {
  Alert,
  Button,
  DialogContent,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  Input,
  ScrollArea,
} from '@ibsys/kit';
import { ComponentProps, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DownloadTemplateEntityType } from '@/entities/download-template';
import { useTemplatePreview } from '@/entities/download-template';
import { getApiErrorMessage } from '@/shared/api';

/** Сущность шаблона по умолчанию: набор переменных диалога один и тот же у обеих сущностей */
const DEFAULT_ENTITY_TYPE: DownloadTemplateEntityType = 'TRANSCRIPT';

type Props = {
  /** Текст шаблона, который проверяется, — введённый в поле, а не сохранённый */
  template: string;
  /** Сущность шаблона; по умолчанию — транскрипция */
  entityType?: DownloadTemplateEntityType;
  /** Проверка недоступна — например, пока шаблон пуст */
  disabled?: boolean;
  /** Переопределение кнопки-триггера */
  trigger?: ComponentProps<typeof Button>;
};

/**
 * Проверка шаблона по диалогу на записи: по идентификатору диалога окно показывает результат
 * подстановки. Ничего не сохраняет и никуда не отправляет — закрытие возвращает пользователя
 * к правке шаблона с тем же введённым текстом.
 */
export const TemplatePreviewDialog: FC<Props> = ({ template, entityType = DEFAULT_ENTITY_TYPE, disabled, trigger }) => {
  const { t } = useTranslation('common');
  const [conversationId, setConversationId] = useState('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { mutateAsync: preview, isPending } = useTemplatePreview();

  // Каждое открытие начинается с чистого окна: прежний результат относится к прежнему шаблону
  const handleOpenChange = (open: boolean) => {
    if (open) {
      setResult(null);
      setError(null);
    }
  };

  const handlePreview = async () => {
    setResult(null);
    setError(null);
    try {
      const response = await preview({ entityType, template, conversationId: Number(conversationId) });
      setResult(response.result);
    } catch (previewError) {
      // Причину называет сервер: диалог не найден либо шаблон не разобран
      setError(getApiErrorMessage(previewError));
    }
  };

  return (
    <DialogRoot onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button type="button" variant="secondary" disabled={disabled} {...trigger}>
          {trigger?.children ?? t('templatePreview.open')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('templatePreview.title')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <Input
              type="number"
              className="w-60"
              label={t('templatePreview.conversationId')}
              value={conversationId}
              onChange={(event) => setConversationId(event.target.value)}
            />
            <Button type="button" onClick={handlePreview} disabled={isPending || !conversationId}>
              {t('templatePreview.run')}
            </Button>
          </div>
          {/* Причина отказа показывается вместо результата: проверка задаёт ровно одно из двух */}
          {error && <Alert type="error" title={error} />}
          {result !== null && (
            <ScrollArea className="max-h-96">
              <pre className="font-mono text-sm whitespace-pre-wrap">{result}</pre>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </DialogRoot>
  );
};
